import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  alpha,
  useTheme,
  Collapse,
  Button,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  FileText,
  Download,
  Eye,
  Send,
  Copy,
  Trash2,
  MoreVertical,
  X,
} from "lucide-react";
import { backendClient } from "../../../../../../../api/backend";
import {
  downloadQuotePdf,
  sendQuoteEmail,
  duplicateQuote,
  deleteQuote,
  updateQuoteStatus,
} from "../../../../../../../redux/actions/facturationAction";
import { useDispatch } from "react-redux";
import { enqueueSnackbar } from "notistack";

const STATUS_CONFIG = {
  draft: { label: "Brouillon", color: "#6b7280" },
  sent: { label: "Envoyé", color: "#3b82f6" },
  accepted: { label: "Accepté", color: "#10b981" },
  rejected: { label: "Refusé", color: "#ef4444" },
  expired: { label: "Expiré", color: "#f59e0b" },
};

const PLAN_LABELS = {
  starter: "Essentiel",
  pro: "Pro",
  enterprise: "Entreprise",
};

const FicheQuotes = ({ orgId, org }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [statusLoading, setStatusLoading] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewQuote, setPreviewQuote] = useState(null);

  const loadQuotes = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await backendClient.getQuotes({ marketing_organization_id: orgId });
      setQuotes(Array.isArray(data) ? data : data.items || []);
    } catch (e) {
      console.error("Failed to load org quotes:", e);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  const handleNewQuote = () => {
    navigate(`/admin/facturation/devis/new?org=${orgId}`);
  };

  const handleViewQuote = (quoteId) => {
    navigate(`/admin/facturation/devis/${quoteId}`);
  };

  const handleDownloadPdf = async (e, quote) => {
    e.stopPropagation();
    setPdfLoading(quote.id);
    try {
      await dispatch(downloadQuotePdf(quote.id, { clientName: quote.client_name, lang: quote.language }));
      enqueueSnackbar("PDF téléchargé", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Erreur PDF", { variant: "error" });
    } finally {
      setPdfLoading(null);
    }
  };

  const handlePreview = async (e, quote) => {
    e.stopPropagation();
    setPdfLoading(quote.id);
    try {
      const blob = await backendClient.downloadQuotePdf(quote.id);
      const prefix = quote.language === "en" ? "Quote" : "Devis";
      const name = quote.client_name ? `${prefix} ${quote.client_name}.pdf` : `${prefix}.pdf`;
      const file = new File([blob], name, { type: "application/pdf" });
      const url = window.URL.createObjectURL(file);
      setPreviewUrl(url);
      setPreviewQuote(quote);
      setPreviewOpen(true);
    } catch (err) {
      enqueueSnackbar("Erreur prévisualisation", { variant: "error" });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleStatusChange = async (e, quoteId) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    setStatusLoading(quoteId);
    try {
      await dispatch(updateQuoteStatus(quoteId, newStatus));
      setQuotes((prev) =>
        prev.map((q) => (q.id === quoteId ? { ...q, status: newStatus } : q))
      );
      enqueueSnackbar(`Statut mis à jour: ${STATUS_CONFIG[newStatus]?.label}`, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    } finally {
      setStatusLoading(null);
    }
  };

  const handleSendEmail = async (e, quote) => {
    e.stopPropagation();
    try {
      await dispatch(sendQuoteEmail(quote.id));
      setQuotes((prev) =>
        prev.map((q) => (q.id === quote.id ? { ...q, status: q.status === "draft" ? "sent" : q.status } : q))
      );
      enqueueSnackbar("Devis envoyé par email", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    }
  };

  const handleDuplicate = async (e, quote) => {
    e.stopPropagation();
    try {
      await dispatch(duplicateQuote(quote.id));
      enqueueSnackbar("Devis dupliqué", { variant: "success" });
      loadQuotes();
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    }
  };

  const handleDelete = async (e, quote) => {
    e.stopPropagation();
    if (quote.status !== "draft") return;
    try {
      await dispatch(deleteQuote(quote.id));
      setQuotes((prev) => prev.filter((q) => q.id !== quote.id));
      enqueueSnackbar("Devis supprimé", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatAmount = (amount, currency = "MAD") => {
    if (!amount) return "—";
    return Number(amount).toLocaleString("fr-FR", { minimumFractionDigits: 2 }) + " " + currency;
  };

  return (
    <Box sx={{ px: 2.5, py: 1.5 }}>
      {/* Section header */}
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", mb: expanded ? 1.5 : 0 }}
        onClick={() => setExpanded((p) => !p)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 1, color: theme.palette.text.secondary }}>
            Devis
          </Typography>
          <Chip
            label={quotes.length}
            size="small"
            sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button
            size="small"
            startIcon={<Plus size={12} />}
            onClick={(e) => { e.stopPropagation(); handleNewQuote(); }}
            sx={{ textTransform: "none", fontSize: "0.7rem", fontWeight: 600, borderRadius: "6px", px: 1, py: 0.25, minHeight: 0 }}
          >
            Nouveau
          </Button>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : quotes.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <FileText size={28} strokeWidth={1} color={theme.palette.text.disabled} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.8rem" }}>
              Aucun devis
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Plus size={12} />}
              onClick={handleNewQuote}
              sx={{ mt: 1, textTransform: "none", fontSize: "0.75rem", borderRadius: "6px" }}
            >
              Créer un devis
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {quotes.map((quote) => {
              const statusCfg = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft;
              return (
                <Box
                  key={quote.id}
                  onClick={() => handleViewQuote(quote.id)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    p: 1.5,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.divider}`,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  {/* Top row: quote number + status + amount */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "monospace", fontSize: "0.8rem" }}>
                        {quote.quote_number}
                      </Typography>
                      <Chip
                        label={PLAN_LABELS[quote.plan] || quote.plan}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.6rem",
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: theme.palette.primary.main,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                      {formatAmount(quote.total_ttc, quote.currency)}
                    </Typography>
                  </Box>

                  {/* Middle row: status + date */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box onClick={(e) => e.stopPropagation()}>
                      {statusLoading === quote.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <FormControl size="small">
                          <Select
                            value={quote.status}
                            onChange={(e) => handleStatusChange(e, quote.id)}
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              height: 24,
                              "& .MuiSelect-select": { py: 0.25, px: 0.75 },
                            }}
                            renderValue={(val) => (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: STATUS_CONFIG[val]?.color }} />
                                <span>{STATUS_CONFIG[val]?.label || val}</span>
                              </Box>
                            )}
                          >
                            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                              <MenuItem key={key} value={key} sx={{ fontSize: "0.8rem" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg.color }} />
                                  {cfg.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                      {formatDate(quote.created_at)}
                    </Typography>
                  </Box>

                  {/* Bottom row: actions */}
                  <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Aperçu PDF">
                      <IconButton size="small" onClick={(e) => handlePreview(e, quote)} disabled={pdfLoading === quote.id} sx={{ p: 0.5 }}>
                        {pdfLoading === quote.id ? <CircularProgress size={12} /> : <Eye size={13} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Télécharger PDF">
                      <IconButton size="small" onClick={(e) => handleDownloadPdf(e, quote)} disabled={pdfLoading === quote.id} sx={{ p: 0.5 }}>
                        <Download size={13} />
                      </IconButton>
                    </Tooltip>
                    {quote.client_contact_email && (
                      <Tooltip title="Envoyer par email">
                        <IconButton size="small" onClick={(e) => handleSendEmail(e, quote)} sx={{ p: 0.5 }}>
                          <Send size={13} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Dupliquer">
                      <IconButton size="small" onClick={(e) => handleDuplicate(e, quote)} sx={{ p: 0.5 }}>
                        <Copy size={13} />
                      </IconButton>
                    </Tooltip>
                    {quote.status === "draft" && (
                      <Tooltip title="Supprimer">
                        <IconButton size="small" onClick={(e) => handleDelete(e, quote)} sx={{ p: 0.5, color: theme.palette.error.main }}>
                          <Trash2 size={13} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Collapse>

      {/* PDF Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
          setPreviewQuote(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh", borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Aperçu — {previewQuote?.quote_number || "Devis"}
          </Typography>
          <IconButton size="small" onClick={() => {
            setPreviewOpen(false);
            if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
            setPreviewQuote(null);
          }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewUrl && (
            <iframe
              src={previewUrl}
              title="Aperçu du devis"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FicheQuotes;
